import {
  Children,
  cloneElement,
  type CSSProperties,
  type FunctionComponent,
  type ReactElement,
} from 'react';

import { getSpacing } from './getSpacing';

export type SpacingType =
  | 'm'
  | 'p'
  | 'mx'
  | 'my'
  | 'mt'
  | 'mb'
  | 'ml'
  | 'mr'
  | 'px'
  | 'py'
  | 'pt'
  | 'pb'
  | 'pl'
  | 'pr'
  | 'none';

export interface SpacingProps {
  /**
   * To define the type of styling:
   * - m: margin
   * - p: padding
   * - x: applies to x-axis
   * - y: applies to y-axis
   * - l: applies to left side
   * - r: applies to right side
   * - t: applies to top side
   * - b: applies to bottom side
   */
  type?: SpacingType;
  size?: string | number;
  unit?: string;
  style?: CSSProperties;
  children: ReactElement[] | ReactElement;
}

/**
 * Spacing is a helper component designed to help with spacing around its
 * children.
 */
export const Spacing: FunctionComponent<SpacingProps> = ({
  type = 'none',
  style,
  size = 0,
  unit = 'px',
  children,
}) => {
  const spacingSize = typeof size !== 'number' ? size : `${size}${unit}`;
  const computedStyle = style || getSpacing(type, spacingSize);
  return (
    <>
      {Children.map(
        children,
        (child: ReactElement<{ style?: CSSProperties }>) =>
          cloneElement(child, {
            style: { ...computedStyle, ...child.props.style },
          }),
      )}
    </>
  );
};

Spacing.displayName = 'Spacing';
